import { useState, useMemo, useCallback } from "react";
import { parseSections } from "./markdown";
import { parseAttachmentRef } from "./markdown";

/**
 * Modal that shows a page's content with paginated sections.
 * Big prev/next buttons, read-only by default with an edit toggle.
 */
export default function PageModal({ page, onClose, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(page?.content || "");
  const [currentSection, setCurrentSection] = useState(0);

  const { intro, sections } = useMemo(() => parseSections(page?.content || ""), [page?.content]);

  // Build "pages" of content: intro (if any) + each section = one slide
  const slides = useMemo(() => {
    const s = [];
    if (intro) s.push({ type: "intro", content: intro });
    for (const sec of sections) s.push({ type: "section", data: sec });
    if (s.length === 0) s.push({ type: "empty" });
    return s;
  }, [intro, sections]);

  const totalSlides = slides.length;
  const canPrev = currentSection > 0;
  const canNext = currentSection < totalSlides - 1;

  const handleSave = useCallback(() => {
    if (onSave) onSave(page.id, editContent);
    setEditing(false);
  }, [onSave, page?.id, editContent]);

  const handleStartEdit = () => {
    setEditContent(page?.content || "");
    setEditing(true);
  };

  if (!page) return null;

  const slide = slides[currentSection];

  return (
    <div className="page-modal-overlay" onClick={onClose}>
      <div className="page-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="page-modal-header">
          <h3>{page.title || "Untitled page"}</h3>
          <div className="page-modal-header-actions">
            {!editing && <button className="btn-sm" onClick={handleStartEdit}>✏️ Edit</button>}
            {editing && <button className="btn-sm btn-save" onClick={handleSave}>💾 Save</button>}
            {editing && <button className="btn-sm" onClick={() => setEditing(false)}>Cancel</button>}
            <button className="page-modal-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Content */}
        <div className="page-modal-body">
          {editing ? (
            <textarea
              className="page-modal-editor"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoFocus
            />
          ) : (
            <div className="page-modal-content">
              {slide.type === "empty" && (
                <p className="page-modal-empty">This page is empty. Click Edit to add content.</p>
              )}
              {slide.type === "intro" && (
                <div className="page-modal-intro">{slide.content}</div>
              )}
              {slide.type === "section" && (
                <div className="page-modal-section">
                  <h4 className="section-heading">{slide.data.heading}</h4>
                  {slide.data.notes && <p className="section-notes">{slide.data.notes}</p>}
                  <ul className="section-items">
                    {slide.data.items.map((item, i) => {
                      const attRef = parseAttachmentRef(item.text);
                      if (attRef) {
                        const att = (page.attachments || []).find((a) => a.id === attRef.attachmentId);
                        return (
                          <li key={i} className="section-item attachment-item">
                            <span className="att-icon">📎</span>
                            {att?.url ? (
                              <a href={att.url} target="_blank" rel="noopener noreferrer">{attRef.filename}</a>
                            ) : (
                              <span className="att-name">{attRef.filename}</span>
                            )}
                          </li>
                        );
                      }
                      return <li key={i} className="section-item">{item.text}</li>;
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!editing && totalSlides > 1 && (
          <div className="page-modal-pagination">
            <button
              className="page-nav-btn"
              onClick={() => setCurrentSection((s) => s - 1)}
              disabled={!canPrev}
            >
              ← Prev
            </button>
            <span className="page-nav-indicator">
              {currentSection + 1} / {totalSlides}
            </span>
            <button
              className="page-nav-btn"
              onClick={() => setCurrentSection((s) => s + 1)}
              disabled={!canNext}
            >
              Next →
            </button>
          </div>
        )}

        {/* Attachments summary */}
        {!editing && (page.attachments || []).length > 0 && slide.type !== "section" && (
          <div className="page-modal-attachments">
            <span className="att-label">Attachments:</span>
            {page.attachments.map((att) => (
              <span key={att.id} className="att-chip">
                {att.url ? (
                  <a href={att.url} target="_blank" rel="noopener noreferrer">📎 {att.filename}</a>
                ) : (
                  <span>📎 {att.filename}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
